import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { localization } from '@ocean/configs'
import { FilterKeyType } from '@ocean/types/dist/filter'
import { FilterUtils } from '@ocean/utils'
import { LaunchEventDetails } from 'types/event'
import { AppThunk } from 'store'
import { ApiAction, MiddlewareApiAction, createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { namespace } from 'modules/virtualClassrooms/common/localization'
import {
  getScheduleDetailsForPresenter,
  updateScheduleRecordings,
  saveVirtualEventScheduleAttendance,
  startVirtualEvent,
  prepareVirtualEvent,
  endVirtualEvent,
  sendEndEventNotification,
} from 'modules/virtualClassrooms/student/PresenterScheduleDetails/api/presenterScheduleDetailsService'
import {
  EventAttendanceTagFilterItem,
  EventAttendanceTagFilter,
  EventAttendanceFilterItems,
  EventParticipant,
  UpdateScheduleRecordingsParams,
  SaveVirtualEventScheduleAttendanceParams,
} from 'modules/virtualClassrooms/student/PresenterScheduleDetails/types'
import {
  PresenterScheduleDetailsState,
  ApiPresenterScheduleDetailsResponse,
  ReloadPresenterScheduleDetailsParams,
  ApiPresenterLaunchEventResponse,
  LaunchEventRequestParams,
} from './types'

const { i18n } = localization

const I18_PATH = `${namespace}:student.presenterScheduleDetails`

const initialState: PresenterScheduleDetailsState = {
  scheduleDetailsData: {},
  eventAttendanceTagFilters: {
    filter: { keyType: FilterKeyType.Number, value: EventAttendanceTagFilter.All },
  },
  updateScheduleRecordingsRequest: {},
  saveVirtualEventScheduleAttendanceRequest: {},
  eventStarted: {},
  eventPrepared: {},
  eventFinished: {},
  sendEndEventNotificationRequest: {},
  isPlayerOpen: false,
  eventParams: {
    eventUrl: '',
    exitUrl: '',
  },
}

const presenterScheduleDetailsSlice = createSlice({
  name: 'virtualClassroomStudentPresenterScheduleDetails',
  initialState,
  reducers: {
    scheduleDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiPresenterScheduleDetailsResponse>>
    ) {
      state.scheduleDetailsData = action.payload
    },
    clearScheduleDetails(state) {
      state.scheduleDetailsData = initialState.scheduleDetailsData
      state.eventParams = initialState.eventParams
    },
    setEventAttendanceTagFilters(state, action: PayloadAction<EventAttendanceTagFilterItem>) {
      const filter = state.eventAttendanceTagFilters[action.payload.name]
      if (filter) filter.value = action.payload.value
    },
    updateScheduleRecordingsRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.updateScheduleRecordingsRequest = action.payload
    },
    saveVirtualEventScheduleAttendanceRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload>
    ) {
      state.saveVirtualEventScheduleAttendanceRequest = action.payload
    },
    startEventProcessed(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiPresenterLaunchEventResponse>>
    ) {
      state.eventStarted = action.payload
    },
    prepareEventProcessed(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiPresenterLaunchEventResponse>>
    ) {
      state.eventPrepared = action.payload
    },
    endVirtualEventRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.eventFinished = action.payload
    },
    sendEndEventNotificationRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.sendEndEventNotificationRequest = action.payload
    },
    setPlayerVisibility(state, action: PayloadAction<boolean>) {
      state.isPlayerOpen = action.payload
    },
    setEventParams(state, action: PayloadAction<LaunchEventDetails>) {
      state.eventParams = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  scheduleDetailsLoaded,
  setEventAttendanceTagFilters,
  updateScheduleRecordingsRequested,
  saveVirtualEventScheduleAttendanceRequested,
  clearScheduleDetails,
  startEventProcessed,
  prepareEventProcessed,
  endVirtualEventRequested,
  sendEndEventNotificationRequested,
  setPlayerVisibility,
  setEventParams,
} = presenterScheduleDetailsSlice.actions

export { setEventAttendanceTagFilters, clearScheduleDetails, setPlayerVisibility }
export default presenterScheduleDetailsSlice.reducer

export const loadPresenterScheduleDetails = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      scheduleDetails: () => getScheduleDetailsForPresenter(scheduleId),
    },
    payloadAction: scheduleDetailsLoaded,
    getResultToKeep: state =>
      state.virtualClassroomStudentPresenterScheduleDetails.scheduleDetailsData.result,
  })
}

export const getFilteredAttendance = createSelector(
  (
    attendance: EventParticipant[] = [],
    numberOfTasks: number | undefined,
    tagFilterValue: EventAttendanceTagFilter | undefined
  ) => ({ attendance, numberOfTasks, tagFilterValue }),

  ({ attendance, numberOfTasks, tagFilterValue }) => {
    const attendanceFilter: EventAttendanceFilterItems = {}
    switch (tagFilterValue) {
      case EventAttendanceTagFilter.Attended:
        attendanceFilter.hasAttendanceConfirmed = { keyType: FilterKeyType.Boolean, value: 1 }
        break
      case EventAttendanceTagFilter.CSTRecordCreated:
        attendanceFilter.hasCstRecord = { keyType: FilterKeyType.Boolean, value: 1 }
        break
      case EventAttendanceTagFilter.CompletedTasks:
        attendanceFilter.tasksCompleted = { keyType: FilterKeyType.Number, value: numberOfTasks }
        break
    }
    return FilterUtils.filterData(attendanceFilter, attendance)
  }
)

const handleReloadPresenterScheduleDetails =
  (response: ApiMiddlewarePayload, params: ReloadPresenterScheduleDetailsParams): AppThunk =>
  async dispatch => {
    const { payloadAction, scheduleId } = params
    dispatch(payloadAction(response))
    if (!response.error) dispatch(loadPresenterScheduleDetails(scheduleId) as MiddlewareApiAction)
  }

export const updateScheduleRecordingsRequest = (
  params: UpdateScheduleRecordingsParams
): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => updateScheduleRecordings(params),
    },
    payloadAction: updateScheduleRecordingsRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    successMessage: i18n.t(`${I18_PATH}.scheduleRecordingsUpdated`),
    nextAction: {
      action: handleReloadPresenterScheduleDetails,
      params: { scheduleId: params.scheduleId, payloadAction: updateScheduleRecordingsRequested },
    },
  })
}

export const saveVirtualEventScheduleAttendanceRequest = (
  params: SaveVirtualEventScheduleAttendanceParams
): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => saveVirtualEventScheduleAttendance(params),
    },
    payloadAction: saveVirtualEventScheduleAttendanceRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    successMessage: i18n.t(`${I18_PATH}.attendanceSavedSuccessfully`),
    nextAction: {
      action: handleReloadPresenterScheduleDetails,
      params: {
        scheduleId: params.scheduleId,
        payloadAction: saveVirtualEventScheduleAttendanceRequested,
      },
    },
  })
}

const handleLaunchEventRequest =
  (
    response: ApiMiddlewarePayload<ApiPresenterLaunchEventResponse>,
    params: LaunchEventRequestParams
  ): AppThunk =>
  async dispatch => {
    const { error, result } = response
    const { payloadAction } = params
    dispatch(payloadAction(response))
    if (!error && result) {
      dispatch(setEventParams(result.status))
      dispatch(setPlayerVisibility(true))
    }
  }

export const startEvent = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      status: () => startVirtualEvent(scheduleId),
    },
    nextAction: {
      action: handleLaunchEventRequest,
      params: { payloadAction: startEventProcessed },
    },
    payloadAction: startEventProcessed,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}

export const prepareEvent = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      status: () => prepareVirtualEvent(scheduleId),
    },
    nextAction: {
      action: handleLaunchEventRequest,
      params: { payloadAction: prepareEventProcessed },
    },
    payloadAction: prepareEventProcessed,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}

const notifyFinishEvent = (scheduleId: Guid): ApiAction =>
  createApiAction({
    apiCalls: {
      notification: () => sendEndEventNotification(scheduleId),
    },
    payloadAction: sendEndEventNotificationRequested,
    enableErrorNotification: true,
  })

const handleFinishEvent =
  (response: ApiMiddlewarePayload, scheduleId: Guid): AppThunk =>
  dispatch => {
    dispatch(endVirtualEventRequested(response))
    if (!response.error) {
      dispatch(notifyFinishEvent(scheduleId) as MiddlewareApiAction)
      dispatch(loadPresenterScheduleDetails(scheduleId) as MiddlewareApiAction)
    }
  }

export const finishEvent = (scheduleId: Guid): ApiAction =>
  createApiAction({
    apiCalls: {
      status: () => endVirtualEvent(scheduleId),
    },
    nextAction: {
      action: handleFinishEvent,
      params: scheduleId,
    },
    payloadAction: endVirtualEventRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
